import axios from "axios";
import config from "./config";
import { cachedPost, bustCache } from "./apiCache";

/**
 * Native profile adapter.
 *
 * The learner identity is membership_user, served by api/profile (own profile),
 * api/profileupdate, api/changepassword. membership_user carries the profile
 * columns (name, profile_img, phone, about, location + country/state/city/geo +
 * location_privacy), so the full profile round-trips on read and write.
 * All FE community-member-profile calls fetch the OWN profile.
 */
function auth(jwt) {
  return { headers: { Authorization: `Bearer ${jwt}` } };
}

/**
 * api/get-myplans -> { orderData:[...], success } — the member's purchased plans.
 *
 * Routed through cachedPost, not raw axios, because TWO independent callers ask
 * for this at once: the My Plans modal on open, and the subscription gate in
 * MembershipShell. Each opening of the modal fired its own request, StrictMode
 * doubled both in dev, and the network tab filled with identical get-myplans
 * calls. Coalescing collapses them into one.
 *
 * `force` is for the reads that must NOT be served from cache — the manual
 * "Check again" on the gate, and the reload after a cancellation.
 */
export async function fetchMyPlans({ funnelId, memberId, jwt, force = false }) {
  const res = await cachedPost(
    `${config.API_URL}/get-myplans`,
    { funnel_id: funnelId, member_id: memberId },
    { ttl: 10000, force, auth: auth(jwt) }
  );
  // orderData is a Collection: a list when keys are sequential, but an OBJECT
  // when the backend keys it. The legacy app always did Object.values(), so do
  // the same — an object here used to silently render "no plans yet".
  const raw = res.data?.orderData;
  const rows = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.values(raw) : [];

  return rows.map((o) => ({
    id: o.membership_product_id,
    product_id: o.product_id,
    invoice_id: o.invoice_id || "",
    title: o.product_title || "Plan",
    price: o.product_price,
    currency: o.currency || "USD",
    payment_type: o.payment_type || "",
    created_at: o.created_at || "",
    // Subscription details the API already returns (getMyplans joins
    // customer_subscriptions) — without these the modal cannot tell a
    // subscription from a one-off, nor offer to cancel one.
    is_subscription: !!o.isSubscription,
    subscription_id: o.subscription_id || null,
    subscription_status: o.subscription_status || null,
    subscription_plan: o.subscription_plan || null,
    // Where to buy this plan again once it has lapsed (getMyplans resolves it
    // per product: sales page, else checkout). '' when there is nowhere to go.
    buy_url: o.buy_url || "",
  }));
}

/**
 * api/cancel-subscription — cancel an active subscription plan.
 *
 * The second call the legacy My Plans page makes; it was missing here, so a
 * member could see a subscription but never stop it. Payload mirrors that page
 * exactly (member_id, member_email, product_id, subscription_id, optional
 * reason capped at 500 chars).
 */
export async function cancelSubscription({
  memberId,
  memberEmail,
  productId,
  subscriptionId,
  reason = "",
  jwt,
}) {
  const body = {
    member_id: `${memberId}`,
    member_email: memberEmail,
    product_id: `${productId}`,
    subscription_id: subscriptionId,
  };
  const trimmed = (reason || "").trim();
  if (trimmed) body.reason = trimmed.slice(0, 500);

  try {
    const res = await axios.post(`${config.API_URL}/cancel-subscription`, body, auth(jwt));

    // The plan list is now stale — a cancellation changes the status and the
    // access window every reader of get-myplans keys off. Drop the cached copy
    // so the next read (the modal's own reload, the gate's re-check) is fresh.
    if (res.data?.success === true) bustCache("get-myplans");

    return {
      success: res.data?.success === true,
      message: res.data?.message || "",
    };
  } catch (err) {
    return {
      success: false,
      message: err?.response?.data?.message || "",
    };
  }
}

/** api/profile -> { error:0, member:{...} } */
export async function fetchProfile({ funnelId, memberId, jwt }) {
  // Fetched by many surfaces (shell, home, feed, modals) — coalesce/cache so
  // they share one call; updateProfile busts this so edits show immediately.
  const res = await cachedPost(
    `${config.API_URL}/profile`,
    { funnel_id: funnelId, member_id: memberId, token: jwt },
    { ttl: 60000, auth: auth(jwt) }
  );
  const u = res.data || {};
  return {
    error: 0,
    member: {
      community_member_id: u.id,
      name: u.name || "",
      email: u.email || "",
      img: u.profile_img || "",
      points: u.tot_points || 0,
      url_token: u.id != null ? String(u.id) : "",
      // Location + contact fields now persist on membership_user.
      phone: u.phone ?? null,
      about: u.about ?? null,
      location: u.location ?? null,
      country: u.country ?? null,
      state: u.state ?? null,
      city: u.city ?? null,
      latitude: u.latitude ?? null,
      longitude: u.longitude ?? null,
      location_privacy: u.location_privacy ?? null,
    },
  };
}

/** api/profileupdate — persists name + profile_img + location/contact fields. */
export async function updateProfile({
  funnelId, memberId, jwt, name, img,
  phone, about, location, country, state, city, latitude, longitude, location_privacy,
}) {
  const res = await axios.post(
    `${config.API_URL}/profileupdate`,
    {
      funnel_id: funnelId, member_id: memberId, token: jwt,
      name, profile_img: img || "",
      phone: phone ?? "",
      about: about ?? "",
      location: location ?? "",
      country: country ?? "",
      state: state ?? "",
      city: city ?? "",
      latitude: latitude ?? "",
      longitude: longitude ?? "",
      location_privacy: location_privacy ?? "",
    },
    auth(jwt)
  );
  const d = res.data || {};
  const ok = d.error !== 1 && d.success !== false;
  // Invalidate the cached profile so the next fetchProfile reflects the edit.
  if (ok) bustCache("/profile");
  return {
    error: ok ? 0 : 1,
    msg: d.message || (ok ? "Profile updated." : "Could not update."),
    // Gamification points the save just earned (the membership's
    // "profile picture" award, granted once on the member's first photo).
    // 0 on every later save — the caller uses it to refresh the point total
    // and say so, instead of the balance silently changing behind them.
    points_awarded: Number(d.points_awarded || 0),
  };
}

/** api/changepassword. */
export async function changePassword({ funnelId, memberId, jwt, currentPassword, newPassword }) {
  const res = await axios.post(
    `${config.API_URL}/changepassword`,
    {
      funnel_id: funnelId,
      member_id: memberId,
      token: jwt,
      currentpassword: currentPassword,
      newpassword: newPassword,
      confirmnewpassword: newPassword,
    },
    auth(jwt)
  );
  const d = res.data || {};
  const ok =
    d.success === true ||
    d.status === 200 ||
    (!!d.message && /success|changed|updated/i.test(d.message) && !/incorrect|wrong|invalid|not match/i.test(d.message));
  return { error: ok ? 0 : 1, msg: d.message || (ok ? "Password changed." : "Could not change password.") };
}

export async function setManualSubscriptionPause({
  memberId,
  memberEmail,
  productId,
  subscriptionId,
  resumeAt = "",
  jwt,
}) {
  const body = {
    member_id: `${memberId}`,
    member_email: memberEmail,
    product_id: `${productId}`,
    subscription_id: subscriptionId,
  };
  if (resumeAt) body.resume_at = resumeAt;
  const endpoint = resumeAt ? "pause-subscription" : "resume-subscription";

  try {
    const res = await axios.post(`${config.API_URL}/${endpoint}`, body, auth(jwt));
    if (res.data?.success === true) bustCache("get-myplans");
    return { success: res.data?.success === true, message: res.data?.message || "" };
  } catch (err) {
    const errors = err?.response?.data?.errors || err?.response?.data;
    const firstError = errors && typeof errors === "object" ? Object.values(errors).flat().find((value) => typeof value === "string") : "";
    return { success: false, message: err?.response?.data?.message || firstError || "" };
  }
}
